// Production reports (WP5.2), over completed batches in a period of days: yield variance per
// product (what came out against the plan and against what the recipe should give) and
// actual against theoretical usage per component (what was taken against the recipes'
// need). Yield variance is reported, never silently turned into prices (Core Model).

import type { usageReportSchema, yieldVarianceReportSchema } from '@enp/domain/api';
import { formatCount } from '@enp/domain/format';
import { addDays } from '@enp/domain/time';
import { Button, cn, EmptyState, focusRing, PageHeader, Qty, Skeleton } from '@enp/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { BarChart3, Download, X } from 'lucide-react';
import { useMemo } from 'react';
import type { z } from 'zod';
import { meQueryOptions } from '../../../auth/session.js';
import {
  BeyondStock,
  cell,
  NARROW_ONLY,
  numCell,
  PlainTable,
  ProductionViews,
  UsageDifference,
  Variance,
  WIDE_COLUMN,
} from '../../../production/parts.js';
import {
  reportsSearchSchema,
  usageReportQuery,
  yieldReportQuery,
  type ReportsSearch,
} from '../../../production/queries.js';
import {
  amountText,
  enteredText,
  USAGE_TAKEN_LABEL,
  USAGE_TOLERANCE_PCT,
  usageBasisNote,
} from '../../../production/words.js';
import { safeText, toCsv, downloadText } from '../../../replay/csv.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { DayRange, FilterSelect } from '../../../stock/components.js';
import { plainDateLabel, accountToday } from '../../../stock/labels.js';
import { clean, locationsQuery } from '../../../stock/queries.js';

export const Route = createFileRoute('/_app/production/reports')({
  validateSearch: reportsSearchSchema,
  component: ReportsPage,
});

type YieldReport = z.infer<typeof yieldVarianceReportSchema>;

/** A yield amount: "32 each" for a counted product, like the batch list (UI5A-204). */
function YieldQty({ value, unit, className }: { value: string; unit: string; className?: string }) {
  return unit === 'ea' ? (
    <span className={cn('whitespace-nowrap tabular-nums', className)}>
      {amountText(value, unit)}
    </span>
  ) : (
    <Qty value={value} unit={unit} maxDecimals={6} className={className} />
  );
}
type UsageReport = z.infer<typeof usageReportSchema>;

const REPORTS = [
  { id: 'yield', label: 'Yield' },
  { id: 'usage', label: 'Ingredient usage' },
] as const;

function yieldCsv(r: YieldReport): string {
  return toCsv([
    [
      'Product',
      'Unit',
      'Batches',
      'Planned',
      'Recipe scaled for',
      'Came out',
      'Against plan',
      'Against plan %',
      'Against recipe',
      'Against recipe %',
    ],
    ...r.rows.map((x) => [
      safeText(x.product.name),
      x.product.base_uom,
      x.batches,
      x.planned_qty,
      x.input_qty,
      x.actual_qty,
      x.vs_planned.qty,
      x.vs_planned.pct ?? '',
      x.vs_recipe.qty,
      x.vs_recipe.pct ?? '',
    ]),
  ]);
}

function usageCsv(r: UsageReport): string {
  return toCsv([
    [
      'Component',
      'Unit',
      'Batches',
      'Recipes needed',
      USAGE_TAKEN_LABEL,
      'Beyond stock',
      'Batches with an entered amount',
      'Entered: recipes needed',
      'Entered: recorded',
      'Entered: difference',
      'Entered: difference %',
    ],
    // The difference only where a baker entered an amount (UI5A-4): elsewhere it is 0 by
    // construction, not a measurement.
    ...r.rows.map((x) => [
      safeText(x.component.name),
      x.component.base_uom,
      x.batches,
      x.theoretical,
      x.consumed,
      x.beyond_stock,
      x.entered_batches,
      x.entered?.theoretical ?? '',
      x.entered?.consumed ?? '',
      x.entered?.difference ?? '',
      x.entered?.difference_pct ?? '',
    ]),
  ]);
}

function YieldTable({ report }: { report: YieldReport }) {
  return (
    <PlainTable
      label="Yield by product"
      head={[
        'Product',
        'Batches',
        'Planned',
        'Scaled for',
        'Came out',
        'Against plan',
        'Against recipe',
      ]}
      align={['left', 'right', 'right', 'right', 'right', 'right', 'right']}
    >
      {report.rows.map((r) => {
        const u = r.product.base_uom;
        return (
          <tr key={r.product.id}>
            <th scope="row" className={cn(cell, 'text-left font-medium text-fg')}>
              <Link to="/production" search={{ product: r.product.id }} className="hover:underline">
                {r.product.name}
              </Link>
            </th>
            <td className={numCell}>{formatCount(r.batches)}</td>
            <td className={numCell}>
              <YieldQty value={r.planned_qty} unit={u} />
            </td>
            <td className={numCell}>
              <YieldQty value={r.input_qty} unit={u} />
            </td>
            <td className={numCell}>
              <YieldQty value={r.actual_qty} unit={u} className="font-medium" />
            </td>
            <td className={numCell}>
              <Variance value={r.vs_planned} unit={u} />
            </td>
            <td className={numCell}>
              <Variance value={r.vs_recipe} unit={u} />
            </td>
          </tr>
        );
      })}
    </PlainTable>
  );
}

// Priority columns below 1360 px (UI5A-103, parts.tsx): "Amounts entered" folds under the
// difference it explains; beyond stock rides on the recorded figure, never a column that can
// scroll away.
function UsageTable({ report }: { report: UsageReport }) {
  return (
    <PlainTable
      label="Ingredient usage"
      head={[
        'Component',
        'Batches',
        'Recipes needed',
        USAGE_TAKEN_LABEL,
        'Amounts entered',
        'Difference where entered',
      ]}
      align={['left', 'right', 'right', 'right', 'left', 'right']}
      columnClass={[undefined, undefined, undefined, undefined, WIDE_COLUMN, undefined]}
    >
      {report.rows.map((r) => {
        const u = r.component.base_uom;
        return (
          <tr key={r.component.id}>
            <th scope="row" className={cn(cell, 'text-left font-medium text-fg')}>
              {r.component.name}
            </th>
            <td className={numCell}>{formatCount(r.batches)}</td>
            <td className={numCell}>
              <Qty value={r.theoretical} unit={u} maxDecimals={6} />
            </td>
            <td className={numCell}>
              <span className="inline-flex flex-col items-end gap-0.5">
                <Qty value={r.consumed} unit={u} maxDecimals={6} className="font-medium" />
                <BeyondStock qty={r.beyond_stock} unit={u} />
              </span>
            </td>
            <td className={cn(cell, 'text-sm', WIDE_COLUMN)}>
              <span className={r.entered_batches === 0 ? 'text-muted' : 'text-fg'}>
                {enteredText(r.entered_batches, r.batches)}
              </span>
            </td>
            <td className={numCell}>
              {r.entered ? (
                <span className="inline-flex flex-col items-end leading-tight">
                  <UsageDifference
                    diff={r.entered.difference}
                    pct={r.entered.difference_pct}
                    unit={u}
                  />
                  <span className="text-xs text-muted">
                    <Qty value={r.entered.consumed} unit={u} maxDecimals={6} /> for{' '}
                    <Qty value={r.entered.theoretical} unit={u} maxDecimals={6} />
                  </span>
                </span>
              ) : (
                <span className="text-muted">
                  <span aria-hidden>—</span>
                  <span className="sr-only">Nothing weighed</span>
                </span>
              )}
              <span className={cn('block text-xs text-muted', NARROW_ONLY)}>
                {enteredText(r.entered_batches, r.batches)}
              </span>
            </td>
          </tr>
        );
      })}
    </PlainTable>
  );
}

function Loading() {
  return (
    <div className="flex flex-col gap-2" aria-busy="true">
      <span role="status" className="sr-only">
        Loading the report…
      </span>
      {Array.from({ length: 4 }, (_, i) => (
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  );
}

function ReportsPage() {
  useDocumentTitle('Production reports');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const today = accountToday(me.account);
  const from = search.from ?? addDays(today, -6);
  const to = search.to ?? today;
  const [lo, hi] = from <= to ? [from, to] : [to, from];
  const which = search.report ?? 'yield';
  const setSearch = (patch: Partial<ReportsSearch>) =>
    void navigate({ search: (prev) => clean({ ...prev, ...patch }), replace: true });

  const locations = useQuery(locationsQuery());
  const branches = useMemo(() => {
    const seen = new Map<string, string>();
    for (const l of locations.data?.items ?? []) seen.set(l.branch.id, l.branch.name);
    return [...seen].map(([value, label]) => ({ value, label }));
  }, [locations.data]);

  const q = { from: lo, to: hi, branch: search.branch, product: search.product };
  const yieldQ = useQuery({ ...yieldReportQuery(q), enabled: which === 'yield' });
  const usageQ = useQuery({ ...usageReportQuery(q), enabled: which === 'usage' });
  const active = which === 'yield' ? yieldQ : usageQ;
  const rows = which === 'yield' ? yieldQ.data?.rows.length : usageQ.data?.rows.length;
  const period =
    lo === hi
      ? plainDateLabel(lo, today)
      : `${plainDateLabel(lo, today)} – ${plainDateLabel(hi, today)}`;

  const download = () => {
    if (which === 'yield' && yieldQ.data) {
      downloadText(`yield-${lo}-to-${hi}.csv`, yieldCsv(yieldQ.data));
    } else if (which === 'usage' && usageQ.data) {
      downloadText(`ingredient-usage-${lo}-to-${hi}.csv`, usageCsv(usageQ.data));
    }
  };

  return (
    <>
      <PageHeader
        title="Production"
        description="Completed batches over a period: what came out, and what they used."
      />
      <ProductionViews current="reports" />

      <div
        className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2"
        role="search"
        aria-label="Report period"
      >
        <DayRange
          idPrefix="production-report"
          from={lo}
          to={hi}
          today={today}
          onChange={(r) => setSearch({ from: r.from, to: r.to })}
          highlight={Boolean(search.from || search.to)}
        />
        {(search.from || search.to) && (
          <Button
            variant="ghost"
            icon={<X aria-hidden />}
            onClick={() => setSearch({ from: undefined, to: undefined })}
          >
            Last 7 days
          </Button>
        )}
        {branches.length > 1 && (
          <FilterSelect
            label="Branch"
            value={search.branch}
            allLabel="Every branch"
            options={branches}
            onChange={(v) => setSearch({ branch: v })}
          />
        )}
      </div>

      <section
        aria-labelledby="report-title"
        className="flex flex-col gap-4 rounded-lg border border-border bg-surface-raised p-5"
      >
        <h2 id="report-title" className="font-display text-lg font-semibold text-fg">
          {which === 'yield' ? 'Yield' : 'Ingredient usage'}, {period}
        </h2>
        {/* The switch and Download sit in one fixed row under the title in both reports, so
            the control just pressed doesn't jump with the description's length (UI5A-204). */}
        <div
          className="flex flex-wrap items-center justify-between gap-3"
          data-testid="report-toolbar"
        >
          <div
            role="group"
            aria-label="Which report"
            className="inline-flex rounded-md border border-border-strong p-0.5"
          >
            {REPORTS.map((r) => (
              <button
                key={r.id}
                type="button"
                aria-pressed={r.id === which}
                onClick={() => setSearch({ report: r.id === 'yield' ? undefined : r.id })}
                className={cn(
                  'h-control-sm rounded-sm px-3 text-sm font-medium text-muted hover:text-fg',
                  r.id === which && 'bg-primary-subtle text-primary-text',
                  focusRing,
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Button
            variant="secondary"
            size="sm"
            icon={<Download aria-hidden />}
            disabled={!active.data || active.isFetching || rows === 0}
            onClick={download}
          >
            Download (CSV)
          </Button>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm text-muted">
            {which === 'yield'
              ? '“Against recipe” compares what came out with what the recipe should give for what was put in.'
              : `${usageBasisNote(usageQ.data?.consumed_basis)} A difference beyond ${USAGE_TOLERANCE_PCT}% either way is marked.`}
            {which === 'usage' && usageQ.data && ` ${formatCount(usageQ.data.batches)} batches.`}
          </p>
        </div>
        {active.isError && !active.data ? (
          <ErrorPanel
            error={active.error}
            titleAs="h2"
            what="The report"
            onRetry={() => void active.refetch()}
          />
        ) : !active.data ? (
          <Loading />
        ) : rows === 0 ? (
          <EmptyState
            compact
            icon={BarChart3}
            title="No completed batches in these days"
            description="Reports count batches once a station completes them. Pick earlier days, or check Production for batches still planned."
            action={
              <Button variant="secondary" asChild>
                <Link to="/production">Go to batches</Link>
              </Button>
            }
          />
        ) : which === 'yield' && yieldQ.data ? (
          <YieldTable report={yieldQ.data} />
        ) : usageQ.data ? (
          <UsageTable report={usageQ.data} />
        ) : null}
      </section>
    </>
  );
}
